// src/auth/AuthContext.jsx
//
// SINGLE source of truth for auth + role + tenant.
// On every auth state change:
//   1. If logged out -> clear state.
//   2. If logged in:
//      a. Check if email is a SuperAdmin (from .env).
//         -> SuperAdmin: role='SuperAdmin', tenantId=null, tenantName=null.
//      b. Otherwise, look up UserProfileMaster where email=user.email.
//         -> If found and status='active': set role/tenantId/tenantName from doc.
//         -> If not found or inactive: signOut + show alert.
//
// ANY component needing auth uses: const { currentUser, role, tenantId, tenantName, loading, logout } = useAuth();
//
// IMPORTANT: This file replaces the broken `./AuthContext` import that
// UserManagement.js and TenantManagement.js were using.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, firestore } from '../components/firebase.config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
};

const getSuperAdminEmails = () =>
  [
    process.env.REACT_APP_ADMIN_EMAIL1,
    process.env.REACT_APP_ADMIN_EMAIL2,
    process.env.REACT_APP_ADMIN_EMAIL3,
  ]
    .filter(Boolean)
    .map((e) => e.toLowerCase().trim());

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null); // 'SuperAdmin' | 'AppAdmin' | 'AppUser' | null
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [profileDocId, setProfileDocId] = useState(null); // UserProfileMaster doc id (null for SuperAdmin)
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const clearState = () => {
    setCurrentUser(null);
    setRole(null);
    setTenantId(null);
    setTenantName(null);
    setProfileDocId(null);
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } finally {
      clearState();
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setAuthError(null);

      if (!user) {
        clearState();
        setLoading(false);
        return;
      }

      const email = (user.email || '').toLowerCase().trim();
      const superAdmins = getSuperAdminEmails();

      // 1. SuperAdmin path - .env only, NEVER touch Firestore.
      if (superAdmins.includes(email)) {
        setCurrentUser(user);
        setRole('SuperAdmin');
        setTenantId(null);
        setTenantName(null);
        setProfileDocId(null);
        setLoading(false);
        return;
      }

      // 2. Regular user - look up in UserProfileMaster by email.
      try {
        const profileQuery = query(
          collection(firestore, 'UserProfileMaster'),
          where('email', '==', email)
        );
        const snap = await getDocs(profileQuery);

        if (snap.empty) {
          // Not whitelisted - sign out immediately.
          await signOut(auth);
          clearState();
          setAuthError('Your account is not authorized. Contact your administrator.');
          setLoading(false);
          return;
        }

        const profileDoc = snap.docs[0];
        const profile = profileDoc.data();

        if (profile.status !== 'active') {
          await signOut(auth);
          clearState();
          setAuthError('Your account is inactive. Contact your administrator.');
          setLoading(false);
          return;
        }

        // Validate role.
        const validRoles = ['AppAdmin', 'AppUser', 'Verifier'];
        if (!validRoles.includes(profile.role)) {
          await signOut(auth);
          clearState();
          setAuthError('Your account has an invalid role. Contact your administrator.');
          setLoading(false);
          return;
        }

        setCurrentUser(user);
        setRole(profile.role);
        setTenantId(profile.tenantId || null);
        setTenantName(profile.tenantName || null);
        setProfileDocId(profileDoc.id);
        setLoading(false);
      } catch (err) {
        console.error('AuthContext: error loading profile', err);
        await signOut(auth);
        clearState();
        setAuthError('Could not load your profile. Try again.');
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const value = {
    currentUser,
    role,
    tenantId,
    tenantName,
    profileDocId,
    loading,
    authError,
    logout,
    // Convenience flags
    isSuperAdmin: role === 'SuperAdmin',
    isAppAdmin: role === 'AppAdmin',
    isAppUser: role === 'AppUser',
    isVerifier: role === 'Verifier',
    isLoggedIn: !!currentUser && !!role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
